import {
	DateTimeChangeableType,
	DurationUnit,
	ProofRequirement,
	RecordedEntryStatus,
	RecordingValidationStatus,
	WorkingTimeTypeCategory,
	WorkingTimeTypeRecordingMode
} from "@/shared/timrTypes";
import {ProjectTime, ReportProjectTime, ReportWorkingTime, ValidationResult, WorkingTimeType} from "@/shared/timr";
import {SecurityStatus} from "@/shared/securityStatus";
import {isSameOrBeforeDay, parseISODate, parseISOZonedDateTime} from "@/shared/utils/datefunctions";

/**
 * Checks if the workingTimeType could be used for add attendance times by the current user
 * @param workingTimeType the workingTimeType
 * @param dateTimeChangeable whether dateTime is changeable or not
 */
export function isWorkingTimeTypeAllowedForAddWorkingTime(workingTimeType: WorkingTimeType, dateTimeChangeable: boolean): boolean {
	return workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime &&
		isWorkingTimeTypeAllowed(workingTimeType, dateTimeChangeable);
}

/**
 * Checks if the workingTimeType could be used for add vacations and absences by the current user
 * @param workingTimeType the workingTimeType
 * @param dateTimeChangeable whether dateTime is changeable or not
 */
export function isWorkingTimeTypeAllowedForAddAbsence(workingTimeType: WorkingTimeType, dateTimeChangeable: boolean): boolean {
	return (workingTimeType.category !== WorkingTimeTypeCategory.AttendanceTime || workingTimeType.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST) &&
		workingTimeType.category !== WorkingTimeTypeCategory.BreakTime &&
		isWorkingTimeTypeAllowed(workingTimeType, dateTimeChangeable);
}

/**
 * Whether a working time type is directly recordable by the user.
 * IMPORTANT Update the java file too: SecurityHelperService.java
 * @param recordingMode the recordingModeUser of the workingTimeType
 */
export function isRecordingModeUserAllowed(recordingMode: WorkingTimeTypeRecordingMode): boolean {
	return recordingMode === WorkingTimeTypeRecordingMode.ALLOWED;
}

/**
 * Whether the current user may start/stop record (i.e. live record) a working time type with the given
 * recording mode. Unlike {@link isRecordingModeUserAllowed}, a BY_REQUEST type may be recorded via start/stop,
 * so only NOT_ALLOWED (admin-only) types are blocked for simple users. Mirrors the server-side query in
 * WorkingTimeTypeDao.findAllWorkingTimeTypesForRecording that builds the recordable working time type list.
 * IMPORTANT Update the java file too: WorkingTimeTypeDao.java
 * @param recordingMode the recordingModeUser of the working time type to record
 */
export function isWorkingTimeTypeRecordable(recordingMode: WorkingTimeTypeRecordingMode): boolean {
	return globals.isAdmin || globals.isGroupLeader ||
		recordingMode !== WorkingTimeTypeRecordingMode.NOT_ALLOWED;
}

/**
 * Checks if the workingTimeType could be used for add workingTimes by the current user
 * @param workingTimeType the workingTimeType
 * @param dateTimeChangeable whether dateTime is changeable or not
 */
export function isWorkingTimeTypeAllowed(workingTimeType: WorkingTimeType, dateTimeChangeable: boolean): boolean {
	if (workingTimeType.archived) {
		return false;
	} else if (workingTimeType.editUnit === DurationUnit.Minutes && isRecordingModeUserAllowed(workingTimeType.recordingModeUser)) {
		return globals.isAdmin ||
			globals.isGroupLeader && globals.workingTimeDateTimeChangeableType === DateTimeChangeableType.ByGroupLeader ||
			dateTimeChangeable;
	} else {
		return globals.isAdmin || globals.isGroupLeader ||
			isRecordingModeUserAllowed(workingTimeType.recordingModeUser) ||
			workingTimeType.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST && SecurityStatus.isWorkingTimeRequestsEnabled();
	}
}

/**
 * Checks if the current user is allowed to switch between the specified working time types
 * IMPORTANT Update the java file too: SecurityHelperService.java
 * @param currentWorkingTimeType the currently selected workingTimeType
 * @param newWorkingTimeType the new workingTimeType
 * @param dateTimeChangeable whether dateTime is changeable or not
 */
export function isWorkingTimeTypeChangeAllowed(currentWorkingTimeType: WorkingTimeType, newWorkingTimeType: WorkingTimeType, dateTimeChangeable: boolean): boolean {
	if (currentWorkingTimeType.id === newWorkingTimeType.id) {
		return true;
	} else if (newWorkingTimeType.archived ||
		!isWorkingTimeTypeRecordingModeChangeAllowed(currentWorkingTimeType.recordingModeUser, newWorkingTimeType.recordingModeUser) ||
		!isEditUnitChangeAllowed(currentWorkingTimeType.editUnit, newWorkingTimeType.editUnit) ||
		!isWorkingTimeTypeCategoryChangeAllowed(currentWorkingTimeType.category, newWorkingTimeType.category)) {
		return false;
	} else {
		// Leaders/admins may always switch. Otherwise the target type must be user-recordable; minute-based types
		// additionally require dateTime to be changeable, while non-minute types require the source type to have
		// been user-recordable as well.
		return globals.isAdmin || globals.isGroupLeader ||
			isRecordingModeUserAllowed(newWorkingTimeType.recordingModeUser) &&
			(newWorkingTimeType.editUnit === DurationUnit.Minutes
				? dateTimeChangeable
				: isRecordingModeUserAllowed(currentWorkingTimeType.recordingModeUser));
	}
}

/**
 * Whether proof documents may already be collected while a working time of this type is running: the type is recorded
 * in minutes, is turned into a working time request when the recording is stopped and actually asks for proof
 * documents. The attachments of such a recording become the proof documents of the created request.
 * IMPORTANT Update the java file too: WorkingTimeService.java
 * @param workingTimeType the workingTimeType to check
 */
export function isRunningWorkingTimeAttachmentEligible(workingTimeType: WorkingTimeType): boolean {
	return workingTimeType.editUnit === DurationUnit.Minutes &&
		workingTimeType.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST &&
		SecurityStatus.isWorkingTimeRequestsEnabled() &&
		workingTimeType.proofRequirement !== ProofRequirement.NOT_APPLICABLE;
}

/**
 * Whether the working time type of a *running* recording may be changed, as far as the two types alone decide it:
 * a running recording must never be moved into a type that collects proof documents, because the whole recorded span
 * would retroactively turn into a working time request when it is stopped.
 *
 * This is only the type-only part of the rule. Changing *away* from a type that collects proof documents is
 * additionally rejected by the backend once attachments have been collected, which the frontend cannot see - the type
 * select is therefore rendered read-only while such a recording is running (see workingTimeStatusPanel.ts).
 * IMPORTANT Update the java file too: WorkingTimeService.java
 * @param currentWorkingTimeType the workingTimeType the recording is running with
 * @param newWorkingTimeType the new workingTimeType
 */
export function isRunningWorkingTimeTypeChangeAllowed(currentWorkingTimeType: WorkingTimeType, newWorkingTimeType: WorkingTimeType): boolean {
	return currentWorkingTimeType.id === newWorkingTimeType.id ||
		!isRunningWorkingTimeAttachmentEligible(newWorkingTimeType);
}

/**
 * Only checks whether it is generally possible to switch between the recordingModes and not whether the user is authorized.
 * IMPORTANT Update the java file too: SecurityHelperService.java
 * @param currentRecordingMode the currently selected recordingMode
 * @param newRecordingMode the new recordingMode
 */
function isWorkingTimeTypeRecordingModeChangeAllowed(currentRecordingMode: WorkingTimeTypeRecordingMode, newRecordingMode: WorkingTimeTypeRecordingMode): boolean {
	return currentRecordingMode !== WorkingTimeTypeRecordingMode.BY_REQUEST &&
		newRecordingMode !== WorkingTimeTypeRecordingMode.BY_REQUEST;
}

/**
 * Only checks whether it is generally possible to switch between the editUnit and not whether the user is authorized.
 * IMPORTANT Update the java file too: SecurityHelperService.java
 * @param currentEditUnit the currently selected editUnit
 * @param newEditUnit the new editUnit
 */
function isEditUnitChangeAllowed(currentEditUnit: DurationUnit, newEditUnit: DurationUnit): boolean {
	return currentEditUnit === DurationUnit.Minutes && newEditUnit === DurationUnit.Minutes ||
		currentEditUnit !== DurationUnit.Minutes && newEditUnit !== DurationUnit.Minutes;
}

/**
 * Only checks whether it is generally possible to switch between the categories and not whether the user is authorized.
 * IMPORTANT Update the java file too: SecurityHelperService.java
 * @param currentCategory the currently selected category
 * @param newCategory the new category
 */
function isWorkingTimeTypeCategoryChangeAllowed(currentCategory: WorkingTimeTypeCategory, newCategory: WorkingTimeTypeCategory): boolean {
	return currentCategory === newCategory;
}

/**
 * Checks if the current user is allowed to delete a given working time
 * If the user wants to delete an entry that is outside the permitted modification period, this is allowed in the front end
 * In contrast to the back end, in order to show the user the reason for the exception
 * IMPORTANT Check if there is an update necessary for the java file: SecurityHelperService.java
 * @param workingTime the working time to delete
 * @param dateTimeChangeable whether dateTime is changeable or not
 */
export function isWorkingTimeDeletable(workingTime: ReportWorkingTime, dateTimeChangeable: boolean): boolean {
	return workingTime.status === RecordedEntryStatus.Changeable &&
		workingTime.hasWorkingTimeRequest === false &&
		// DEVNOTE: WorkingTimeDateSpan is only deletable if all the other WorkingTimes are editable as well
		(workingTime.workingTimeDateSpanId === null || workingTime.workingTimeDateSpanEditable) &&
		// recordingMode gates direct edits/deletes regardless of editUnit
		workingTime.workingTimeTypeRecordingModeUser !== WorkingTimeTypeRecordingMode.BY_REQUEST &&
		(isRecordingModeUserAllowed(workingTime.workingTimeTypeRecordingModeUser) || globals.isGroupLeader) &&
		(workingTime.editUnit !== DurationUnit.Minutes || dateTimeChangeable);
}

/**
 * Checks if a given working time may be opened in the working time edit dialog directly
 * @param workingTime the working time to edit
 */
export function isWorkingTimeEditAllowed(workingTime: Pick<ReportWorkingTime, "editUnit" | "status" | "workingTimeTypeRecordingModeUser">): boolean {
	return workingTime.editUnit === DurationUnit.Minutes && workingTime.status === RecordedEntryStatus.Changeable &&
		(globals.isGroupLeader || isRecordingModeUserAllowed(workingTime.workingTimeTypeRecordingModeUser));
}

/**
 * Checks if a given project time may be opened in the project time edit dialog
 * @param projectTime the project time to edit
 */
export function isProjectTimeEditAllowed(projectTime: Pick<ReportProjectTime, "status" | "isTaskLocked">): boolean {
	return projectTime.status === RecordedEntryStatus.Changeable && !projectTime.isTaskLocked;
}

/**
 * Checks if a given project time starts within the locked period of its task
 * @param projectTime the project time to check
 */
export function isProjectTimeTaskLocked(projectTime: ProjectTime): boolean {
	const lockDate = parseISODate(projectTime.task.lockDateInherited);
	return SecurityStatus.isLockTaskAllowed()
		&& lockDate !== null
		&& isSameOrBeforeDay(parseISOZonedDateTime(projectTime.start), lockDate);
}

/**
 * Returns true if the user has permission to use set the save and approve button
 * for dialogs without user selections userSelectionEnabled is true (like e.g. at working time recording - add dialog)
 *
 * @param userSelectionEnabled {boolean} true if the userSelection field is visible
 * @param selectedUserId {number} id of the selected user
 * @param isGroupLeaderForThemself {boolean} if the group member is a group leader himself (leader and member of a team)
 */
export function permissionForSaveAndApproveButton(userSelectionEnabled: boolean, selectedUserId: number, isGroupLeaderForThemself: boolean): boolean {
	return globals.isWorkingTimeRequestsOverrideAllowed && permissionForSaveButton(userSelectionEnabled, selectedUserId, isGroupLeaderForThemself);
}

/**
 * Returns true if the user has permission to use save button
 * for dialogs without user selections userSelectionEnabled is true (like e.g. at working time recording - add dialog)
 *
 * @param userSelectionEnabled {boolean} true if the userSelection field is visible
 * @param selectedUserId {number} id of the selected user
 * @param isGroupLeaderForThemself {boolean} if the group member is a group leader himself (leader and member of a team)
 */
export function permissionForSaveButton(userSelectionEnabled: boolean, selectedUserId: number, isGroupLeaderForThemself: boolean) {
	return globals.isAuthorizedForAllUsers ||
		((!userSelectionEnabled || globals.userId === selectedUserId) ? isGroupLeaderForThemself : globals.isGroupLeader);
}

/**
 * Returns true if the given validation is relevant for the current user. (relevant validations have a different visualisation)
 * Relevant are the following validations: OPEN if validation of current user, OPEN/ACKNOWLEDGED of other users
 * TODO: this is used in the frontend, but there are still places where the validation relevance is determined in the backend by nesting this has to be unified, see see TIMR-9318
 *
 * @param validation the validation result to check
 * @returns true if the validation is relevant, false otherwise
 */
export function isValidationRelevant(validation: ValidationResult): boolean {
	const isCurrentUserValidation = validation.user.id === globals.userId;
	return isCurrentUserValidation && validation.status === RecordingValidationStatus.OPEN ||
		!isCurrentUserValidation && [RecordingValidationStatus.OPEN, RecordingValidationStatus.ACKNOWLEDGED].some(status => status === validation.status);
}

/**
 * Determines whether a status number should be shown on the provided recording status and permissions.
 * For the status action for multiple records - via bulk operations - currentStatus may be null
 *
 * @param newStatus the new status to set
 * @param currentStatus the status of the current recording
 * @param isTaskLeader 	whether it should be considered that the user is a task leader
 * 						DEVNOTE: It is very likely that the use and provisioning of the variable "isTaskLeader" is basically not implemented correctly and consistently
 *						 			Sometimes globals.isTaskLeader is provided; sometimes the provided value depends on the (report) filter selection which is updated via user endpoint;
 *						 		Sometimes it is provided for non-project times but should generally not be relevant outside of project time; see TIMR-11693
 */
export function shouldAddStatusNumber(newStatus: RecordedEntryStatus, currentStatus: RecordedEntryStatus | null, isTaskLeader: boolean): boolean {
	// currentStatus is null when changing status for multiple records via bulk operations
	if (currentStatus === null) {
		return globals.isGroupLeader || isTaskLeader || newStatus === RecordedEntryStatus.Locked;
	}

	return (currentStatus !== RecordedEntryStatus.Archived || globals.isAuthorizedForAllUsers) &&
		(globals.isGroupLeader || isTaskLeader || currentStatus === RecordedEntryStatus.Changeable && newStatus === RecordedEntryStatus.Locked);
}

/**
 * Filters the provided list of working time types to include only those
 * that are allowed for adding a vacation, based on specific conditions.
 *
 * @param workingTimeTypes - The list of working time types to be evaluated.
 * @param forGroup - A flag determining whether the list is for a group or not (reporting other users). This is relevant to evaluate if date time is changeable. Default is true.
 * @return The filtered list of working time types that are allowed for adding a vacation.
 */
export function getWorkingTimeTypesForAddAbsence(workingTimeTypes: WorkingTimeType[], forGroup: boolean = true): WorkingTimeType[] {
	return workingTimeTypes.filter((workingTimeType) => {
		return isWorkingTimeTypeAllowedForAddAbsence(workingTimeType, isWorkingTimeDateTimeChangeAllowed(forGroup));
	});
}

/**
 * Filters and returns a list of working time types allowed for adding working time.
 *
 * @param workingTimeTypes - The list of working time types to be evaluated.
 * @param forGroup - A flag determining whether the list is for a group or not (reporting other users). This is relevant to evaluate if date time is changeable. Default is true.
 * @return The filtered list of working time types that are allowed for adding a vacation.
 */
export function getWorkingTimeTypesForAddWorkingTime(workingTimeTypes: WorkingTimeType[], forGroup: boolean = true): WorkingTimeType[] {
	return workingTimeTypes.filter((workingTimeType) => {
		return isWorkingTimeTypeAllowedForAddWorkingTime(workingTimeType, isWorkingTimeDateTimeChangeAllowed(forGroup));
	});
}

export function isWorkingTimeDateTimeChangeAllowed(forGroup: boolean = false, userId: number = null, allowedUserIds: number[] = []): boolean {
	return dateTimeChangeAllowed(globals.workingTimeDateTimeChangeable, globals.workingTimeDateTimeChangeableType, forGroup, userId, allowedUserIds);
}

export function isProjectTimeDateTimeChangeAllowed(forGroup: boolean = false, userId: number = null, allowedUserIds: number[] = []): boolean {
	return dateTimeChangeAllowed(globals.projectTimeDateTimeChangeable, globals.projectTimeDateTimeChangeableType, forGroup, userId, allowedUserIds);
}

export function isDriveLogDateTimeChangeAllowed(forGroup: boolean = false, userId: number = null, allowedUserIds: number[] = []): boolean {
	return dateTimeChangeAllowed(globals.driveLogDateTimeChangeable, globals.driveLogDateTimeChangeableType, forGroup, userId, allowedUserIds);
}

export function dateTimeChangeAllowed(dateTimeChangeable: boolean, dateTimeChangeableType: DateTimeChangeableType, forGroup: boolean, userId: number, allowedUserIds: number[]): boolean {
	if (dateTimeChangeable) {
		return true;
	} else if (dateTimeChangeableType === DateTimeChangeableType.ByGroupLeader && globals.isGroupLeader && forGroup) {
		return true;
	} else if (dateTimeChangeableType === DateTimeChangeableType.ByGroupLeader && globals.isGroupLeader) {
		return allowedUserIds.includes(userId);
	}
	return false;
}
